import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Avatar,
    Box,
    Chip,
    Paper,
    Slide,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import LanguageIcon from '@mui/icons-material/LanguageOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import ApartmentIcon from '@mui/icons-material/ApartmentOutlined';
import { Icon } from '@iconify/react/dist/iconify.js';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// DD/MM/YYYY, or N/A - these rows used to print today's date for every vendor.
const fmtDate = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? 'N/A' : formatIstDate(d);
};

const ViewVendorDetailsModal = ({ open, handleClose, vendor }) => {
    if (!vendor) return null;

    const getValue = (value) => value && value !== '-' ? value : 'N/A';

    const InfoItem = ({ icon, label, value }) => (
        <Box sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 1, alignItems: 'flex-start', py: 1 }}>
            <Box sx={{ color: 'primary.main', mt: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.3, textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-word' }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header with Gradient Background */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #487fff 0%, #6366f1 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 0, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon icon="mdi:storefront-outline" width={24} height={24} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                                Vendor Details
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {/* Profile Section in Header */}
                <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar
                            sx={{
                                width: 72,
                                height: 72,
                                bgcolor: 'white',
                                color: 'primary.main',
                                fontSize: '1.75rem',
                                fontWeight: 700,
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                            }}
                        >
                            {vendor?.name?.charAt(0)?.toUpperCase() || 'V'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                                {getValue(vendor?.name)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label={`ID: ${vendor?.id || 'N/A'}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={vendor?.status ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        bgcolor: vendor?.status ? '#22c55e' : '#ef4444',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                    }}
                                />
                                <Chip
                                    label={vendor?.category || 'N/A'}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                {/* Main Content Grid */}
                <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                        {/* Contact Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Contact Information
                                </Typography>
                            </Box>
                            <InfoItem icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Representative" value={getValue(vendor?.representative)} />
                            <InfoItem icon={<PhoneIcon sx={{ fontSize: 18 }} />} label="Phone" value={getValue(vendor?.phone)} />
                            <InfoItem icon={<EmailIcon sx={{ fontSize: 18 }} />} label="Office Email" value={getValue(vendor?.email)} />
                            <InfoItem icon={<LanguageIcon sx={{ fontSize: 18 }} />} label="Website" value={getValue(vendor?.website)} />
                            <InfoItem icon={<PhoneIcon sx={{ fontSize: 18 }} />} label="Alternate Phone" value={getValue(vendor?.alternatePhone)} />
                            <InfoItem icon={<LocationOnIcon sx={{ fontSize: 18 }} />} label="Address" value={getValue([vendor?.address, vendor?.pincode].filter(Boolean).join(', '))} />
                        </Paper>

                        {/* Vendor Details */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Vendor Details
                                </Typography>
                            </Box>
                            <InfoItem icon={<DescriptionIcon sx={{ fontSize: 18 }} />} label="Category" value={getValue(vendor?.category)} />
                            <InfoItem icon={<LocationOnIcon sx={{ fontSize: 18 }} />} label="City" value={getValue(vendor?.city)} />
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="PAN No" value={getValue(vendor?.panNumber)} />
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="GST No" value={getValue(vendor?.gstNumber)} />
                            <InfoItem icon={<ApartmentIcon sx={{ fontSize: 18 }} />} label="Community" value={getValue(vendor?.society)} />
                            <InfoItem icon={<DescriptionIcon sx={{ fontSize: 18 }} />} label="GL Account" value={getValue(vendor?.glCode)} />
                        </Paper>

                        {/* Tax Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Tax Information
                                </Typography>
                            </Box>
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="TAN No" value={getValue(vendor?.tanNumber)} />
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="TDS Applicable" value={vendor?.tdsApplicable ? 'Yes' : 'No'} />
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="Payment Terms" value={getValue(vendor?.paymentTerms)} />
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="MSME Status" value={getValue(vendor?.msmeStatus)} />
                        </Paper>

                        {/* Additional Information */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <CalendarTodayIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Additional Information
                                </Typography>
                            </Box>
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="Contract" value={getValue([vendor?.contractStatus, vendor?.contractStart && `from ${fmtDate(vendor.contractStart)}`, vendor?.contractEnd && `to ${fmtDate(vendor.contractEnd)}`].filter(Boolean).join(' '))} />
                            <InfoItem icon={<BusinessIcon sx={{ fontSize: 18 }} />} label="RCM Applicable" value={vendor?.rcmApplicable ? 'Yes' : 'No'} />
                            <InfoItem icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />} label="Created On" value={fmtDate(vendor?.createdAt)} />
                            <InfoItem icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />} label="Updated On" value={fmtDate(vendor?.updatedAt)} />
                        </Paper>
                    </Box>

                    {/* Societies Section */}
                    <Paper elevation={0} sx={{ p: 2.5, mt: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <ApartmentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Societies with this Vendor
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Societies where this vendor is registered
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Chip label="1 Total" size="small" variant="filled" />
                            <Chip label="1 Active" size="small" variant="filled" />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: '#f9fafb' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white', fontSize: '0.875rem', fontWeight: 700 }}>1</Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                Onboarding society
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Chip label="Active" size="small" sx={{ bgcolor: '#22c55e', color: 'white' }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                    <Chip label="GL: 02-11-14" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    <Chip label="Bill: ₹54002" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    <Chip label="Tax: IGST" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    <Chip label="TDS Applicable" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    <Chip label="Date: 13 Jan 2026" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    <Chip label="Portal Access" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ViewVendorDetailsModal;
